/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/types/critical-variables.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import { useQuery } from '@apollo/client/react';
import { NamedItemType } from 'types/graphql-global-types';
import { FieldChangeGrid } from 'views/pages/admin/audit-log/change-grid';
import AuditEnumValues from 'views/pages/admin/audit-log/definitions/enums';
import {
    getFieldChangeItems,
    getProcessedFieldChangeItems,
    removePropertiesFromObject,
} from 'views/pages/admin/audit-log/service';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import { getNamedItemsByType } from '@queries/generated/common.generated';

const propArray = [
    'InternalOnly',
    'NeedsMonitoring',
    'WasIsmp',
    'ResponsibleVendorId',
    'EDCEntryId',
    'TypeId',
    'IsIsmp',
];

const CriticalVariableRecordChangeGrid = ({ newItem, oldItem, oldRecord, newRecord, record }: any) => {
    const refactoredOldRecord = oldRecord
        ? {
              ...oldRecord,
              ...removePropertiesFromObject(oldRecord, propArray),
              vendor: oldItem?.vendor != null ? oldItem?.vendor?.name : null,
              cTQEDCEntry: oldItem?.cTQEDCEntry != null ? oldItem?.cTQEDCEntry?.name : null,
              cTQType: oldItem?.cTQType != null ? oldItem?.cTQType?.name : null,
              IsCritical: oldItem?.isCritical != null ? oldItem.isCritical : null,
              Type: oldRecord.Type === 0 ? undefined : oldRecord.Type,
          }
        : oldRecord;

    const refactoredNewRecord = newRecord
        ? {
              ...newRecord,
              ...removePropertiesFromObject(newRecord, propArray),
              vendor: newItem?.vendor != null ? newItem?.vendor?.name : null,
              cTQEDCEntry: newItem?.cTQEDCEntry != null ? newItem?.cTQEDCEntry?.name : null,
              cTQType: newItem?.cTQType != null ? newItem?.cTQType?.name : null,
              IsCritical: newItem?.isCritical != null ? newItem.isCritical : null,
              Type: newRecord.Type === 0 ? undefined : newRecord.Type,
          }
        : newRecord;

    const { data: vendorData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: {
            type: NamedItemType.VENDOR,
            timestamp: record.created,
        },
        fetchPolicy: 'no-cache',
    });

    const enums: any = { Type: AuditEnumValues.CriticalVariableType };
    const associations: any = {
        Controls: {
            itemField: 'controls',
            name: 'Control',
            nameResolver: (control: any) => control?.description,
        },
        AssociatedRisks: {
            itemField: 'associatedRisks',
            name: 'Risk',
            nameResolver: (risk: any) => risk?.statement,
        },
        AssociatedFunctionalDataReviews: {
            itemField: 'associatedFunctionalDataReviews',
            name: 'Functional Data Review',
            nameResolver: (fdr: any) => fdr?.description,
        },
    };

    const formatVendorIds = (recordData: any, index: number) => {
        if (!recordData) {
            return null;
        }

        const data = recordData as { $oid: string };
        const vendor = vendorData?.namedItemsByType?.find((rv) => rv?.id === data.$oid);

        if (!vendor) {
            return null;
        }

        return `Order: ${index + 1}\n RecordId: ${vendor?.recordId}\nName: ${vendor?.name}`;
    };

    const arrayOfObjectsV2: any = {
        VendorIds: {
            valueResolver: formatVendorIds,
            compareField: '$oid',
        },
    };

    const items = getFieldChangeItems(refactoredOldRecord, refactoredNewRecord, null, (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
            enums,
            associations,
            arrayOfObjectsV2,
        }),
    );

    return <FieldChangeGrid items={items} />;
};

export { CriticalVariableRecordChangeGrid };
